import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

export type PedagogyPriorityRequestStatus = 'open' | 'assigned' | 'done' | 'failed' | 'cancelled'
export type PedagogyPriorityRequestSource = 'franchisee' | 'lead'

export interface PedagogyPriorityTeacherOption {
  teacherId: string
  fullName: string
  status: 'training' | 'working'
}

export interface PedagogyPrioritySessionOption {
  itemId: number
  teacherId: string | null
  teacherName: string | null
  directionName: string | null
  studioInternalId: number
  studioNumber: string
  studioAddress: string
  cityName: string
  trainingDate: string
  trainingTime: string
}

export interface PedagogyPriorityRequestSnapshot {
  requestKey: string
  status: PedagogyPriorityRequestStatus
  teacherId: string | null
  teacherName: string | null
  studioInternalId: number | null
  studioNumber: string | null
  studioAddress: string | null
  directionName: string | null
  trainingDate: string | null
  availableFrom: string | null
  itemId: number | null
  trainingTime: string | null
  attemptCount: number
  createdAt: string
  assignedAt: string | null
  completedAt: string | null
  reviewId: number | null
  reviewKey: string | null
  reviewTeacherId: string | null
  reviewStudioInternalId: number | null
}

export interface PartnerPedagogyPriorityRequestContext {
  partnerNumber: number
  accessKind: 'owner' | 'manager' | 'senior_teacher' | 'management_company'
  periodId: number | null
  periodStart: string | null
  periodEnd: string | null
  canCreate: boolean
  latestRequest: PedagogyPriorityRequestSnapshot | null
  teachers: PedagogyPriorityTeacherOption[]
  sessions: PedagogyPrioritySessionOption[]
}

export interface PartnerPedagogyPriorityRequestOptions {
  teachers: PedagogyPriorityTeacherOption[]
  sessions: PedagogyPrioritySessionOption[]
}

export interface SavePartnerPedagogyPriorityRequestInput {
  studioInternalId: number
  requestKey?: string | null
  teacherId: string
  directionName?: string | null
  targetStudioInternalId?: number | null
  trainingDate?: string | null
  itemId?: number | null
}

export interface PedagogyPriorityPartnerOption {
  partnerNumber: number
  partnerName: string
  availableSessions: number
}

export interface PedagogyPriorityManagementOptions {
  partnerNumber: number
  partnerName: string
  sessions: PedagogyPrioritySessionOption[]
}

export interface CreatePedagogyPriorityRequestInput {
  partnerNumber: number
  studioInternalId?: number | null
  teacherId?: string | null
  directionName?: string | null
  trainingDate?: string | null
  itemId?: number | null
  availableFrom?: string | null
}

export interface PedagogyPriorityRequestRegistryRow {
  requestKey: string
  requestSource: PedagogyPriorityRequestSource
  requestedByName: string | null
  partnerNumber: number
  partnerName: string
  teacherId: string | null
  teacherName: string | null
  studioInternalId: number | null
  studioNumber: string | null
  studioAddress: string | null
  directionName: string | null
  trainingDate: string | null
  availableFrom: string | null
  trainingTime: string | null
  status: PedagogyPriorityRequestStatus
  attemptCount: number
  createdAt: string
  assignedAt: string | null
  completedAt: string | null
  reviewId: number | null
  reviewKey: string | null
  reviewTeacherId: string | null
  reviewStudioInternalId: number | null
  lastFailureCode: string | null
  totalCount: number
}

function formatError(error: unknown): string {
  if (!error) return 'Unknown error'
  if (error instanceof Error) return error.message
  if (typeof error === 'object') {
    const value = error as { message?: string; details?: string; hint?: string; code?: string }
    if (value.code === '57014') return 'Данные не успели загрузиться. Попробуйте ещё раз.'
    if (/failed to fetch|networkerror|network request failed/i.test([value.message, value.details].filter(Boolean).join(' '))) {
      return 'Не удалось связаться с сервером. Проверьте интернет и попробуйте ещё раз.'
    }
    return [value.message, value.details, value.hint, value.code].filter(Boolean).join(' | ') || JSON.stringify(error)
  }
  return String(error)
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
}

function asRows(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value) ? value.map(asRecord) : []
}

function text(value: unknown): string {
  return value == null ? '' : String(value)
}

function nullableText(value: unknown): string | null {
  return value == null || value === '' ? null : String(value)
}

function nullableNumber(value: unknown): number | null {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function mapSession(value: unknown): PedagogyPrioritySessionOption {
  const row = asRecord(value)
  return {
    itemId: Number(row.item_id),
    teacherId: nullableText(row.teacher_id),
    teacherName: nullableText(row.teacher_name),
    directionName: nullableText(row.direction_name),
    studioInternalId: Number(row.studio_internal_id),
    studioNumber: text(row.studio_number),
    studioAddress: text(row.studio_address),
    cityName: text(row.city_name),
    trainingDate: text(row.training_date),
    trainingTime: text(row.training_time),
  }
}

function mapRequestSnapshot(value: unknown): PedagogyPriorityRequestSnapshot | null {
  if (!value) return null
  const row = asRecord(value)
  const requestKey = nullableText(row.request_key)
  if (!requestKey) return null
  return {
    requestKey,
    status: text(row.status) as PedagogyPriorityRequestStatus,
    teacherId: nullableText(row.teacher_id),
    teacherName: nullableText(row.teacher_name),
    studioInternalId: nullableNumber(row.studio_internal_id),
    studioNumber: nullableText(row.studio_number),
    studioAddress: nullableText(row.studio_address),
    directionName: nullableText(row.direction_name),
    trainingDate: nullableText(row.training_date),
    availableFrom: nullableText(row.available_from),
    itemId: nullableNumber(row.item_id),
    trainingTime: nullableText(row.training_time),
    attemptCount: Number(row.attempt_count ?? 0),
    createdAt: text(row.created_at),
    assignedAt: nullableText(row.assigned_at),
    completedAt: nullableText(row.completed_at),
    reviewId: nullableNumber(row.review_id),
    reviewKey: nullableText(row.review_key),
    reviewTeacherId: nullableText(row.review_teacher_id),
    reviewStudioInternalId: nullableNumber(row.review_studio_internal_id),
  }
}

export async function getPartnerPedagogyPriorityRequestContext(
  studioInternalId: number
): Promise<PartnerPedagogyPriorityRequestContext> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_partner_pedagogy_priority_request_context', {
      p_studio_internal_id: studioInternalId,
    })
  )
  if (error) throw new Error(formatError(error))

  const payload = asRecord(data)
  return {
    partnerNumber: Number(payload.partner_number ?? 0),
    // Fail closed: partner request UI is available only to an owner or an active senior teacher.
    accessKind: payload.access_kind === 'manager'
      ? 'manager'
      : payload.access_kind === 'owner'
      ? 'owner'
      : payload.access_kind === 'senior_teacher'
        ? 'senior_teacher'
        : 'management_company',
    periodId: nullableNumber(payload.period_id),
    periodStart: nullableText(payload.period_start),
    periodEnd: nullableText(payload.period_end),
    canCreate: Boolean(payload.can_create),
    latestRequest: mapRequestSnapshot(payload.latest_request),
    teachers: asRows(payload.teachers).map(row => ({
      teacherId: text(row.teacher_id),
      fullName: text(row.full_name),
      status: row.status === 'training' ? 'training' : 'working',
    })),
    sessions: asRows(payload.sessions).map(mapSession),
  }
}

export async function getPartnerPedagogyPriorityRequestOptions(
  studioInternalId: number
): Promise<PartnerPedagogyPriorityRequestOptions> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_partner_pedagogy_priority_request_options', {
      p_studio_internal_id: studioInternalId,
    })
  )
  if (error) throw new Error(formatError(error))

  const payload = asRecord(data)
  return {
    teachers: asRows(payload.teachers).map(row => ({
      teacherId: text(row.teacher_id),
      fullName: text(row.full_name),
      status: row.status === 'training' ? 'training' : 'working',
    })),
    sessions: asRows(payload.sessions).map(mapSession),
  }
}

export async function savePartnerPedagogyPriorityRequest(
  input: SavePartnerPedagogyPriorityRequestInput
): Promise<string> {
  const supabase = getSupabase()
  const args = {
    p_studio_internal_id: input.studioInternalId,
    p_teacher_id: input.teacherId,
    p_direction_name: input.directionName?.trim() || null,
    p_target_studio_internal_id: input.targetStudioInternalId ?? null,
    p_training_date: input.trainingDate ?? null,
    p_item_id: input.itemId ?? null,
  }

  if (input.requestKey) {
    const { data, error } = await withAuthRecovery(() =>
      supabase.rpc('update_partner_pedagogy_priority_request', {
        ...args,
        p_request_key: input.requestKey,
      })
    )
    if (error) throw new Error(formatError(error))
    if (!data) throw new Error('Запрос уже нельзя изменить')
    return input.requestKey
  }

  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('create_partner_pedagogy_priority_request', args)
  )
  if (error) throw new Error(formatError(error))
  return text(data)
}

export async function cancelPartnerPedagogyPriorityRequest(
  studioInternalId: number,
  requestKey: string
): Promise<boolean> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('cancel_partner_pedagogy_priority_request', {
      p_studio_internal_id: studioInternalId,
      p_request_key: requestKey,
    })
  )
  if (error) throw new Error(formatError(error))
  return Boolean(data)
}

export async function listPartnerPedagogyRequestedReviewKeys(
  studioInternalId: number,
  teacherId: string
): Promise<string[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_partner_pedagogy_requested_review_keys', {
      p_studio_internal_id: studioInternalId,
      p_teacher_id: teacherId,
    })
  )
  if (error) throw new Error(formatError(error))
  return asRows(data).map(row => text(row.review_key)).filter(Boolean)
}

export async function listPedagogyPriorityRequestPartners(): Promise<PedagogyPriorityPartnerOption[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_pedagogy_priority_request_partners')
  )
  if (error) throw new Error(formatError(error))
  return asRows(data).map(row => ({
    partnerNumber: Number(row.partner_number),
    partnerName: text(row.partner_name),
    availableSessions: Number(row.available_sessions ?? 0),
  }))
}

export async function getPedagogyPriorityRequestManagementOptions(
  partnerNumber: number
): Promise<PedagogyPriorityManagementOptions> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('get_pedagogy_priority_request_management_options', {
      p_partner_number: partnerNumber,
    })
  )
  if (error) throw new Error(formatError(error))
  const payload = asRecord(data)
  return {
    partnerNumber: Number(payload.partner_number ?? partnerNumber),
    partnerName: text(payload.partner_name),
    sessions: asRows(payload.sessions).map(mapSession),
  }
}

export async function createPedagogyPriorityRequest(
  input: CreatePedagogyPriorityRequestInput
): Promise<string> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('create_pedagogy_priority_request', {
      p_partner_number: input.partnerNumber,
      p_studio_internal_id: input.studioInternalId ?? null,
      p_teacher_id: input.teacherId ?? null,
      p_direction_name: input.directionName?.trim() || null,
      p_training_date: input.trainingDate ?? null,
      p_item_id: input.itemId ?? null,
      p_available_from: input.availableFrom ?? null,
    })
  )
  if (error) throw new Error(formatError(error))
  return text(data)
}

export async function cancelPedagogyPriorityRequest(requestKey: string): Promise<boolean> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('cancel_pedagogy_priority_request', { p_request_key: requestKey })
  )
  if (error) throw new Error(formatError(error))
  return Boolean(data)
}

export async function listPedagogyPriorityRequests(
  periodId: number | null = null,
  limit = 100,
  offset = 0
): Promise<PedagogyPriorityRequestRegistryRow[]> {
  const supabase = getSupabase()
  const { data, error } = await withAuthRecovery(() =>
    supabase.rpc('list_pedagogy_priority_requests', {
      p_period_id: periodId,
      p_limit: limit,
      p_offset: offset,
    })
  )
  if (error) throw new Error(formatError(error))

  return asRows(data).map(row => ({
    requestKey: text(row.request_key),
    requestSource: text(row.request_source) as PedagogyPriorityRequestSource,
    requestedByName: nullableText(row.requested_by_name),
    partnerNumber: Number(row.partner_number),
    partnerName: text(row.partner_name),
    teacherId: nullableText(row.teacher_id),
    teacherName: nullableText(row.teacher_name),
    studioInternalId: nullableNumber(row.studio_internal_id),
    studioNumber: nullableText(row.studio_number),
    studioAddress: nullableText(row.studio_address),
    directionName: nullableText(row.direction_name),
    trainingDate: nullableText(row.training_date),
    availableFrom: nullableText(row.available_from),
    trainingTime: nullableText(row.training_time),
    status: text(row.status) as PedagogyPriorityRequestStatus,
    attemptCount: Number(row.attempt_count ?? 0),
    createdAt: text(row.created_at),
    assignedAt: nullableText(row.assigned_at),
    completedAt: nullableText(row.completed_at),
    reviewId: nullableNumber(row.review_id),
    reviewKey: nullableText(row.review_key),
    reviewTeacherId: nullableText(row.review_teacher_id),
    reviewStudioInternalId: nullableNumber(row.review_studio_internal_id),
    lastFailureCode: nullableText(row.last_failure_code),
    totalCount: Number(row.total_count ?? 0),
  }))
}
